import { all, takeLatest, put, call } from 'redux-saga/effects';
import {
  VERIFY_COMPANY_CODE_REQUEST,
  VERIFY_COMPANY_CODE_SUCCESS,
  VERIFY_COMPANY_CODE_ERROR,
  GENERATE_TEMP_PASSWORD_REQUEST,
  GENERATE_TEMP_PASSWORD_SUCCESS,
  GENERATE_TEMP_PASSWORD_ERROR,
  LOGIN_TEMP_PASSWORD_REQUEST,
  LOGIN_TEMP_PASSWORD_SUCCESS,
  LOGIN_TEMP_PASSWORD_ERROR,
  CHANGE_PASSWORD_REQUEST,
  CHANGE_PASSWORD_SUCCESS,
  CHANGE_PASSWORD_ERROR,
  SETUP_PROFILE_REQUEST,
  SETUP_PROFILE_SUCCESS,
  SETUP_PROFILE_ERROR,
  CREATE_PASSWORD_REQUEST,
  CREATE_PASSWORD_SUCCESS,
  CREATE_PASSWORD_ERROR,
} from '../reducers/OnboardingReducer';

import { getSimplifiedError } from '../../utils/error';
import { onboardingRequest } from '../../utils/http';

// Company Code Verification
function verifyCompanyCode(payload) {
  return onboardingRequest.post('/verify-company-code/', payload);
}

export function* handleVerifyCompanyCode({ payload }) {
  try {
    const { data } = yield call(verifyCompanyCode, payload);
    yield put({
      type: VERIFY_COMPANY_CODE_SUCCESS,
      data: data,
    });
  } catch (error) {
    // Handle the specific API response format for onboarding
    let errorMessage = 'Invalid company code. Please contact support.';
    
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      if (responseData.errors) {
        const errors = responseData.errors;
        // Check for non_field_errors first (general errors)
        if (errors.non_field_errors && errors.non_field_errors.length > 0) {
          errorMessage = errors.non_field_errors[0];
        } else if (errors.company_code && errors.company_code.length > 0) {
          errorMessage = errors.company_code[0];
        } else {
          // Get the first error from any field
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      } else if (responseData.message) {
        errorMessage = responseData.message;
      }
    } else {
      errorMessage = getSimplifiedError(error) || errorMessage;
    }
    
    yield put({
      type: VERIFY_COMPANY_CODE_ERROR,
      error: errorMessage,
    });
  }
}

// Generate Temporary Password
function generateTempPassword(payload) {
  return onboardingRequest.post('/generate-temp-password/', payload);
}

export function* handleGenerateTempPassword({ payload, callback }) {
  try {
    const { data } = yield call(generateTempPassword, payload);
    yield put({
      type: GENERATE_TEMP_PASSWORD_SUCCESS,
      data: data,
    });
    
    // Call the callback if provided
    if (callback) {
      callback(data);
    }
  } catch (error) {
    let errorMessage = 'Failed to generate temporary password. Please try again.';
    
    if (error.response && error.response.data) {
      if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.errors) {
        const errors = error.response.data.errors;
        const firstError = Object.values(errors)[0];
        errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
      }
    }
    
    yield put({
      type: GENERATE_TEMP_PASSWORD_ERROR,
      error: errorMessage,
    });
  }
}

// Login with Temporary Password
function loginTempPassword(payload) {
  return onboardingRequest.post('/login-temp-password/', payload);
}

export function* handleLoginTempPassword({ payload, callback }) {
  try {
    const { data } = yield call(loginTempPassword, payload);
    yield put({
      type: LOGIN_TEMP_PASSWORD_SUCCESS,
      data: data,
    });
    
    // Call the callback if provided
    if (callback) {
      callback(data);
    }
  } catch (error) {
    let errorMessage = 'Invalid temporary password. Please try again.';
    
    console.log('Login temp password error:', error);
    
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      console.log('Error response data:', responseData);
      
      // Handle different error response formats
      if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.errors) {
        const errors = responseData.errors;
        // Check for non_field_errors first (general authentication errors)
        if (errors.non_field_errors && errors.non_field_errors.length > 0) {
          errorMessage = errors.non_field_errors[0];
        } else if (errors.temp_password && errors.temp_password.length > 0) {
          errorMessage = errors.temp_password[0];
        } else if (errors.session_id && errors.session_id.length > 0) {
          errorMessage = errors.session_id[0];
        } else {
          // Get the first error from any field
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      } else if (responseData.detail) {
        errorMessage = responseData.detail;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.log('Final error message:', errorMessage);
    
    yield put({
      type: LOGIN_TEMP_PASSWORD_ERROR,
      error: errorMessage,
    });
  }
}

// Change Password
function changePassword(payload) {
  return onboardingRequest.post('/change-password/', payload);
}

export function* handleChangePassword({ payload }) {
  try {
    const { data } = yield call(changePassword, payload);
    yield put({
      type: CHANGE_PASSWORD_SUCCESS,
      data: data,
    });
  } catch (error) {
    let errorMessage = 'Failed to change password. Please try again.';
    
    console.log('Change password error:', error);
    
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      console.log('Error response data:', responseData);
      
      // Handle different error response formats
      if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.errors) {
        const errors = responseData.errors;
        // Check for non_field_errors first (general errors)
        if (errors.non_field_errors && errors.non_field_errors.length > 0) {
          errorMessage = errors.non_field_errors[0];
        } else if (errors.new_password && errors.new_password.length > 0) {
          errorMessage = errors.new_password[0];
        } else if (errors.confirm_password && errors.confirm_password.length > 0) {
          errorMessage = errors.confirm_password[0];
        } else if (errors.session_id && errors.session_id.length > 0) {
          errorMessage = errors.session_id[0];
        } else {
          // Get the first error from any field
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      } else if (responseData.detail) {
        errorMessage = responseData.detail;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.log('Final error message:', errorMessage);
    
    yield put({
      type: CHANGE_PASSWORD_ERROR,
      error: errorMessage,
    });
  }
}

// Setup Profile
function setupProfile(payload) {
  return onboardingRequest.post('/setup-profile/', payload);
}

// Create Password
function createPassword(payload) {
  return onboardingRequest.post('/create-password/', payload);
}

export function* handleSetupProfile({ payload }) {
  try {
    console.log('🔍 Saga: handleSetupProfile called with payload:', payload);
    console.log('🔍 Saga: Session ID being sent:', payload.session_id);
    console.log('🔍 Saga: Session ID type:', typeof payload.session_id);
    console.log('🔍 Saga: Session ID length:', payload.session_id?.length);
    
    const { data } = yield call(setupProfile, payload);
    console.log('✅ Saga: Profile setup successful, response:', data);
    yield put({
      type: SETUP_PROFILE_SUCCESS,
      data: data,
    });
  } catch (error) {
    let errorMessage = 'Failed to setup profile. Please try again.';
    
    console.log('Setup profile error:', error);
    
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      console.log('Error response data:', responseData);
      
      // Handle different error response formats
      if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.errors) {
        const errors = responseData.errors;
        // Check for non_field_errors first (general errors)
        if (errors.non_field_errors && errors.non_field_errors.length > 0) {
          errorMessage = errors.non_field_errors[0];
        } else if (errors.first_name && errors.first_name.length > 0) {
          errorMessage = `First name: ${errors.first_name[0]}`;
        } else if (errors.last_name && errors.last_name.length > 0) {
          errorMessage = `Last name: ${errors.last_name[0]}`;
        } else if (errors.date_of_birth && errors.date_of_birth.length > 0) {
          errorMessage = `Date of birth: ${errors.date_of_birth[0]}`;
        } else if (errors.session_id && errors.session_id.length > 0) {
          errorMessage = errors.session_id[0];
        } else {
          // Get the first error from any field
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      } else if (responseData.detail) {
        errorMessage = responseData.detail;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    console.log('Final error message:', errorMessage);
    
    yield put({
      type: SETUP_PROFILE_ERROR,
      error: errorMessage,
    });
  }
}

export function* handleCreatePassword({ payload }) {
  try {
    console.log('🔍 Saga: handleCreatePassword called with payload:', payload);
    console.log('🔍 Saga: Session ID being sent:', payload.session_id);
    console.log('🔍 Saga: Password being sent:', payload.password);
    
    const { data } = yield call(createPassword, payload);
    console.log('✅ Saga: Password creation successful, response:', data);
    yield put({
      type: CREATE_PASSWORD_SUCCESS,
      data: data,
    });
  } catch (error) {
    let errorMessage = 'Failed to create password. Please try again.';
    
    console.log('Create password error:', error);
    
    if (error.response && error.response.data) {
      const responseData = error.response.data;
      console.log('Error response data:', responseData);
      
      // Handle different error response formats
      if (responseData.message) {
        errorMessage = responseData.message;
      } else if (responseData.errors) {
        const errors = responseData.errors;
        // Check for non_field_errors first (general errors)
        if (errors.non_field_errors && errors.non_field_errors.length > 0) {
          errorMessage = errors.non_field_errors[0];
        } else if (errors.password && errors.password.length > 0) {
          errorMessage = `Password: ${errors.password[0]}`;
        } else if (errors.confirm_password && errors.confirm_password.length > 0) {
          errorMessage = `Confirm Password: ${errors.confirm_password[0]}`;
        } else if (errors.session_id && errors.session_id.length > 0) {
          errorMessage = errors.session_id[0];
        } else {
          // Get the first error from any field
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      } else if (responseData.detail) {
        errorMessage = responseData.detail;
      } else if (responseData.error) {
        errorMessage = responseData.error;
      }
    } else {
      errorMessage = getSimplifiedError(error);
    }
    
    yield put({
      type: CREATE_PASSWORD_ERROR,
      error: errorMessage,
    });
  }
}

export default all([
  takeLatest(VERIFY_COMPANY_CODE_REQUEST, handleVerifyCompanyCode),
  takeLatest(GENERATE_TEMP_PASSWORD_REQUEST, handleGenerateTempPassword),
  takeLatest(LOGIN_TEMP_PASSWORD_REQUEST, handleLoginTempPassword),
  takeLatest(CHANGE_PASSWORD_REQUEST, handleChangePassword),
  takeLatest(SETUP_PROFILE_REQUEST, handleSetupProfile),
  takeLatest(CREATE_PASSWORD_REQUEST, handleCreatePassword),
]);
